import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { slideDown, slideUp } from 'es6-slide-up-down';
import BioCard from './cards/BioCard';
import { device } from '../../utils/styled-component-mixins';
import ServerStylesConditionalWrapper from '../shared/ServerStylesConditionalWrapper';
import { useLocalization } from '../custom-hooks/useLocalization';
import { withLocalization } from '../shared/Context/LocalizationContext';

const LeadershipTeamOverviewStyled = styled.div`
  margin: 8rem 0 0 0;

  @media ${device.desktop} {
    margin: 16rem 8.2rem 0 8.2rem;
  }

  h2 {
    margin-bottom: 4rem;
    text-align: center;
  }

  .custom-select {
    cursor: pointer;
    background-color: var(--white);
    border: 1px solid var(--select-border);
    border-radius: 0.25em;
    display: flex;
    justify-content: center;
    position: relative;

    &-options-wrapper {
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      line-height: 2.4rem;

      @media ${device.desktop} {
        align-items: center;
      }

      .combobox {
        @media ${device.desktop} {
          margin-left: 13rem;
          max-width: 44.5rem;
        }
      }

      label {
        margin-right: 0.8rem;
      }

      .label-wrapper {
        display: flex;
      }
    }

    .material-icons {
      color: var(--cta-blue);
    }

    .combo-menu {
      padding-top: 4rem;

      @media ${device.desktop} {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;

        > div {
          display: flex;
          flex: 1 1 35%;
        }
      }

      div[role='option'] {
        font-weight: normal;
        margin-bottom: 1.6rem;

        &:last-child {
          margin-bottom: 0;
        }

        @media ${device.desktop} {
          &:nth-last-child(-n + 2) {
            margin-bottom: 0;
            align-items: flex-start;
          }
        }

        &[disabled] span {
          color: var(--charcoal-2);
          cursor: default;
        }

        &:not([disabled]) span {
          color: var(--cta-blue);
          border-bottom: 1px solid transparent;
          color: var(--button-tertiary);
          padding: 0;
          position: relative;
          text-decoration: none;
          text-transform: none;
          transition: background-size 0.3s;

          &::after {
            background-color: var(--button-tertiary);
            bottom: 0;
            content: '';
            display: block;
            height: 1px;
            left: 0;
            position: absolute;
            transition: width 0.3s;
            width: 0;
          }

          &:hover {
            &::after {
              width: 100%;
            }
          }
        }
      }
    }

    .combo-menu-wrapper {
      --options-max-width: 16.5rem;
      display: none;
      max-width: 16.5rem;

      @media ${device.desktop} {
        max-width: none;
      }
    }

    .selected-option {
      display: flex;

      > span:first-child {
        border-bottom: 2px solid transparent;
        color: var(--cta-blue);
        font-size: 1.8rem;
        font-weight: 700;
        user-select: none;
        position: relative;
        line-height: 2.4rem;
        transition: background-size 0.3s;

        &::after {
          background-color: var(--button-tertiary);
          bottom: 0;
          content: '';
          display: block;
          height: 2px;
          left: 0;
          position: absolute;
          transition: width 0.3s;
          width: 0;
        }

        &:hover {
          &::after {
            width: 100%;
          }
        }
      }
    }
  }

  .team-list {
    margin-top: 8rem;

    > ul {
      display: flex;
      flex-direction: column;

      li {
        padding: 0;
      }
    }

    h3 {
      font-size: 2.6rem;
      font-weight: 800;
      line-height: 3.2rem;
      margin-bottom: 4rem;
      text-align: center;

      @media ${device.desktop} {
        font-size: 3rem;
        line-height: 4rem;
        margin-bottom: 6.4rem;
      }
    }
  }

  .team-category {
    @media ${device.desktop} {
      margin-bottom: 4rem;
    }

    &:first-child {
      h3 {
        margin-bottom: 8rem;

        @media ${device.desktop} {
          margin-bottom: 6.4rem;
        }
      }
    }

    li {
      margin-bottom: 6.6rem;
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      &::before {
        content: none;
      }
    }
  }

  .bio-card-info {
    h4 {
      font-size: 2.2rem;
      font-weight: 800;
      line-height: 3.2rem;
      margin-bottom: 0;
    }

    @media ${device.desktop} {
      margin-top: 1.6rem;
    }
  }
`;

const KeyContactsList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    &::before {
      content: none;
    }
  }

  @media ${device.desktop} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  li {
    @media ${device.desktop} {
      flex-basis: calc(25% - 3.9rem);
      margin: 0 2.6rem 6.6rem 2.6rem;

      &:nth-child(4n + 4),
      :last-child {
        margin-right: 0;
      }
      &:nth-child(4n + 1),
      :first-child {
        margin-left: 0;
      }
    }
  }
`;

const LeadershipTeamOverview = (props) => {
  const { leadershipTeamListHeading } = props;
  const [leaderTeamNames, setLeaderTeamNames] = useState([]);
  const [selectedLeaderTeam, setSelectedLeaderTeam] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLeaderTeamName, setSelectedLeaderTeamName] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [viewBioText, setViewBioText] = useState('View Bio');
  const [filterLabel, setFilterLabel] = useState('Show:');
  const optionsWrapper = useRef(null);
  const selectRef = useRef(null);
  const { resourceStrings } = useLocalization();

  const leadershipTeamOverviewFilterLabel =
    resourceStrings['BDO.LeadershipTeamOverview.FilterLabel'];
  const leadershipTeamOverviewViewBioText = resourceStrings['BDO.LeaderShipTeamOverview.ViewBio'];

  useEffect(() => {
    if (leadershipTeamOverviewViewBioText) {
      setViewBioText(leadershipTeamOverviewViewBioText);
    }
    if (leadershipTeamOverviewFilterLabel) {
      setFilterLabel(leadershipTeamOverviewFilterLabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLeaderTeamCategories = (teamName) => {
    const url = `/api/leader-categories/${teamName}`;
    if (teamName !== selectedLeaderTeamName) {
      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          setSelectedLeaderTeam(data);
        })
        // eslint-disable-next-line no-console
        .catch((error) => console.log(error));
    }
  };

  useEffect(() => {
    const url = '/api/leader-teams';
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        setLeaderTeamNames(data.leaderTeams);
        setSelectedLeaderTeamName(data.leaderTeams[0]);
        if (!window.location.hash) {
          getLeaderTeamCategories(data.leaderTeams[0]);
        }
      })
      // eslint-disable-next-line no-console
      .catch((error) => console.log(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoading(false);
  }, [selectedLeaderTeam]);

  const setComboMenuMobileMaxWidth = () => {
    const selectElementWidth = selectRef?.current?.offsetWidth;
    const comboMenuWrapper = document.querySelector('.combo-menu-wrapper');
    comboMenuWrapper?.style?.setProperty('--options-max-width', `${selectElementWidth}px`);
  };

  useEffect(() => {
    setComboMenuMobileMaxWidth();
  });

  const toggleDropdown = () => {
    setShowOptions(!showOptions);
    if (showOptions) {
      slideUp(optionsWrapper.current);
      selectRef.current.setAttribute('aria-expanded', 'false');
    } else {
      slideDown(optionsWrapper.current);
      selectRef.current.setAttribute('aria-expanded', 'true');
    }
  };

  const setUrlHash = (hash) => {
    if (window.history.pushState) {
      window.history.pushState(null, null, hash);
    } else {
      window.location.hash = hash;
    }
  };

  const handleOptionClick = (e) => {
    setSelectedLeaderTeamName(e.target.innerText);
    getLeaderTeamCategories(e.target.innerText);
    setUrlHash(`#${e.target.innerText.toLowerCase()}`);
  };

  const handleSelectKeyPress = (e) => {
    if (e.key === 'Enter') {
      toggleDropdown();
    }
  };

  const handleOptionKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleOptionClick(e);
    }
  };

  const initActiveFilter = () => {
    const url = window.location.hash;
    const uri = url?.split('#')[1];
    const headerHeight = document.querySelector('header').offsetHeight;
    const teamList = document.querySelector('.team-list');

    leaderTeamNames.forEach((teamName) => {
      if (teamName.toLowerCase() === decodeURI(uri)) {
        setSelectedLeaderTeamName(teamName);
        getLeaderTeamCategories(teamName);
        window.scroll(0, teamList.offsetTop - headerHeight);
      }
    });
  };

  useEffect(() => {
    initActiveFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderTeamNames]);

  return (
    <ServerStylesConditionalWrapper>
      <LeadershipTeamOverviewStyled>
        <h2>{leadershipTeamListHeading}</h2>
        {!isLoading && (
          <>
            <div className="custom-select">
              <div className="custom-select-options-wrapper">
                <div className="label-wrapper">
                  <label id="leader-team-list-id" htmlFor="combobox" aria-label="show leader teams">
                    {filterLabel}
                  </label>
                  <div
                    className="selected-option"
                    aria-controls="leader-team-list"
                    aria-expanded="false"
                    aria-haspopup="listbox"
                    aria-labelledby="leader-team-list-id"
                    role="combobox"
                    tabIndex="0"
                    ref={selectRef}
                    onClick={toggleDropdown}
                    onKeyPress={handleSelectKeyPress}
                  >
                    <span>{selectedLeaderTeamName}</span>
                    <span className="material-icons">
                      {showOptions ? 'expand_less' : 'expand_more'}
                    </span>
                  </div>
                </div>
                <div className="combobox">
                  <div
                    className="combo-menu-wrapper"
                    ref={optionsWrapper}
                    role="listbox"
                    aria-labelledby="leader-team-list-id"
                    tabIndex="-1"
                  >
                    <div className="combo-menu">
                      {leaderTeamNames.map((leaderTeamName) => (
                        <div
                          aria-selected={
                            selectedLeaderTeamName === leaderTeamName ? 'true' : 'false'
                          }
                          key={leaderTeamName}
                          role="option"
                          id={leaderTeamName}
                          onClick={handleOptionClick}
                          onKeyPress={handleOptionKeyPress}
                          value={leaderTeamName}
                          disabled={leaderTeamName === selectedLeaderTeamName}
                          tabIndex={leaderTeamName === selectedLeaderTeamName ? '-1' : '0'}
                        >
                          <span value={leaderTeamName}>{leaderTeamName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="team-list container container-padding-default">
              <ul className="non-bdo-style-list">
                {selectedLeaderTeam &&
                  selectedLeaderTeam.map((teamCategory) => (
                    <li className="team-category" key={teamCategory.pageTitle}>
                      <h3 id={teamCategory.pageTitle}>{teamCategory.pageTitle}</h3>
                      <KeyContactsList
                        aria-labelledby={teamCategory.pageTitle}
                        className="non-bdo-style-list"
                        key={teamCategory.nodeId}
                      >
                        {teamCategory.bioPages.map((bio) => (
                          <BioCard
                            key={bio.nodeId}
                            {...bio}
                            name={bio.bioName}
                            viewBioText={viewBioText}
                            headingTagNumber={4}
                          />
                        ))}
                      </KeyContactsList>
                    </li>
                  ))}
              </ul>
            </div>
          </>
        )}
      </LeadershipTeamOverviewStyled>
    </ServerStylesConditionalWrapper>
  );
};

export default withLocalization(LeadershipTeamOverview);
